import React, { useState, useEffect } from 'react';
import { Activity, AlertCircle, Battery, Clock, TrendingUp, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ASSET_TYPES, FLOORS } from '../mockData';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    ready: 0,
    inUse: 0,
    toClean: 0,
    outOfOrder: 0,
    lowBattery: 0,
    openTickets: 0
  });

  useEffect(() => {
    const storedAssets = localStorage.getItem('e2-assets');
    if (storedAssets) {
      const parsedAssets = JSON.parse(storedAssets);
      setAssets(parsedAssets);
      
      // Calculate stats
      const newStats = {
        total: parsedAssets.length,
        ready: parsedAssets.filter(a => a.status === 'ready').length,
        inUse: parsedAssets.filter(a => a.status === 'in-use').length,
        toClean: parsedAssets.filter(a => a.status === 'to-clean').length,
        outOfOrder: parsedAssets.filter(a => a.status === 'out-of-order').length,
        lowBattery: parsedAssets.filter(a => a.batteryLevel < 20).length,
        openTickets: parsedAssets.reduce((sum, a) => sum + (a.maintenanceTickets?.filter(t => t.status === 'open').length || 0), 0)
      };
      setStats(newStats);
    }
  }, []);

  // Calculate utilization by type
  const utilizationByType = ASSET_TYPES.map(type => {
    const typeAssets = assets.filter(a => a.type === type.value);
    const inUseCount = typeAssets.filter(a => a.status === 'in-use').length;
    const percentage = typeAssets.length > 0 ? Math.round((inUseCount / typeAssets.length) * 100) : 0;
    return {
      type: type.value,
      total: typeAssets.length,
      inUse: inUseCount,
      percentage
    };
  });

  // Calculate busiest zones
  const busiestZones = FLOORS.map(floor => {
    const floorAssets = assets.filter(a => a.floor === floor.id);
    const inUseCount = floorAssets.filter(a => a.status === 'in-use').length;
    return {
      floor: `${floor.name} - ${floor.department}`,
      count: inUseCount,
      total: floorAssets.length
    };
  }).sort((a, b) => b.count - a.count);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('dashboard.title')}</h1>
          <p className="page-subtitle">{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="dashboard-grid">
        <Card className="kpi-card">
          <CardHeader>
            <CardTitle className="kpi-title">{t('dashboard.totalAssets')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="kpi-value">{stats.total}</div>
            <p className="kpi-label">{t('dashboard.trackedDevices')}</p>
          </CardContent>
        </Card>

        <Card className="kpi-card kpi-ready">
          <CardHeader>
            <CardTitle className="kpi-title">{t('dashboard.ready')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="kpi-value" style={{ color: '#10B981' }}>{stats.ready}</div>
            <p className="kpi-label">{t('dashboard.availableForUse')}</p>
          </CardContent>
        </Card>

        <Card className="kpi-card kpi-in-use">
          <CardHeader>
            <CardTitle className="kpi-title">{t('dashboard.inUse')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="kpi-value" style={{ color: '#3B82F6' }}>{stats.inUse}</div>
            <p className="kpi-label">{t('dashboard.currentlyActive')}</p>
          </CardContent>
        </Card>

        <Card className="kpi-card kpi-alert">
          <CardHeader>
            <CardTitle className="kpi-title">{t('dashboard.needsAttention')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="kpi-value" style={{ color: '#F45053' }}>{stats.toClean + stats.outOfOrder}</div>
            <p className="kpi-label">{stats.toClean} {t('dashboard.toClean')}, {stats.outOfOrder} {t('dashboard.broken')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="secondary-stats">
        <Card className="stat-card">
          <CardContent className="stat-content">
            <Battery size={24} className="stat-icon" style={{ color: '#F45053' }} />
            <div>
              <div className="stat-value">{stats.lowBattery}</div>
              <div className="stat-label">{t('dashboard.lowBattery')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="stat-content">
            <AlertCircle size={24} className="stat-icon" style={{ color: '#F59E0B' }} />
            <div>
              <div className="stat-value">{stats.openTickets}</div>
              <div className="stat-label">{t('dashboard.openTickets')}</div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="stat-content">
            <Clock size={24} className="stat-icon" style={{ color: '#10B981' }} />
            <div>
              <div className="stat-value">~2.3 min</div>
              <div className="stat-label">{t('dashboard.avgTime')}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Utilization Chart */}
        <Card className="chart-card">
          <CardHeader>
            <CardTitle className="chart-title">
              <TrendingUp size={20} />
              {t('dashboard.utilization')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bar-chart">
              {utilizationByType.map((item, idx) => (
                <div key={idx} className="bar-item">
                  <div className="bar-label">{t(`assetTypes.${item.type}`)}</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill" 
                      style={{ 
                        width: `${item.percentage}%`,
                        backgroundColor: '#3B82F6'
                      }}
                    >
                      <span className="bar-text">{item.inUse}/{item.total}</span>
                    </div>
                  </div>
                  <div className="bar-percentage">{item.percentage}%</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Busiest Zones */}
        <Card className="chart-card">
          <CardHeader>
            <CardTitle className="chart-title">
              <MapPin size={20} />
              {t('dashboard.busiestZones')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="zone-list">
              {busiestZones.map((zone, idx) => (
                <div key={idx} className="zone-item">
                  <div className="zone-rank">#{idx + 1}</div>
                  <div className="zone-info">
                    <div className="zone-name">{zone.floor}</div>
                    <div className="zone-stats">{zone.count} assets in use / {zone.total} total</div>
                  </div>
                  <div className="zone-badge">
                    {zone.count > 0 ? '🔥' : '✓'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      <Card className="recommendations-card">
        <CardHeader>
          <CardTitle className="chart-title">
            <Activity size={20} />
            {t('dashboard.recommendations')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="recommendations-list">
            {(stats.openTickets === 0 && stats.outOfOrder === 0 && stats.lowBattery === 0 && stats.toClean <= 2) && (
              <div className="recommendation-item success">
                <Activity size={20} />
                <div>
                  <div className="recommendation-title">{t('dashboard.allOperational')}</div>
                  <div className="recommendation-text">{t('dashboard.noIssues')}</div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
